import { useState } from "react"
import { useDropzone } from "react-dropzone"
import ReactModal from "react-modal"
import TextareaAutosize from "react-textarea-autosize"
import { ReactComponent as RedCrossIcon } from "../assets/icons/red-cross-icon.svg"

const MAX_CHARACTERS = 2000

type AutogenModalProps = ReactModal.Props & {
  isLoadingAIResponse: boolean
  onConfirm: (autogeneratePrompt: string, filesToUpload: File[]) => void
}

export const AutogenModal = (props: AutogenModalProps) => {
  const { isLoadingAIResponse, onConfirm, ...reactModalProps } = props

  const [autogeneratePrompt, setAutogeneratePrompt] = useState("")
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [fileUploadPreviews, setFileUploadPreviews] = useState<string[]>([])

  const handleConfirm = () => {
    onConfirm(autogeneratePrompt, filesToUpload)
    setAutogeneratePrompt("")
    setFilesToUpload([])
  }

  const handleFileUploadDrop = (files: File[]) => {
    setFilesToUpload(files)

    Promise.all(
      files.map((file) => {
        if (file.type.startsWith("image/")) {
          return new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = (e) => resolve((e.target?.result as string) || "")
            reader.readAsDataURL(file)
          })
        } else {
          return Promise.resolve("")
        }
      })
    ).then(setFileUploadPreviews)
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "application/pdf": [".pdf"],
    },
    onDrop: handleFileUploadDrop,
  })

  const remainingCharacters =
    MAX_CHARACTERS - autogeneratePrompt.replace(/\s+/g, " ").length

  return (
    <ReactModal
      {...reactModalProps}
      style={{
        content: {
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          padding: 10,
          zIndex: 10,
          borderRadius: 5,
          bottom: "auto",
        },
        overlay: {
          zIndex: 10,
          background: "rgba(0,0,0,0.1)",
        },
      }}
    >
      {isLoadingAIResponse && (
        <div className="loading-indicator">
          <span className="loading-indicator-span-1"></span>
          <span className="loading-indicator-span-2"></span>
        </div>
      )}

      {!isLoadingAIResponse && (
        <div
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              handleConfirm()
            }
          }}
          style={{ height: "calc(100% - 40px)" }}
        >
          <TextareaAutosize
            style={{
              display: "block",
              border: "1px solid #898989",
              borderRadius: 5,
              width: "calc(100% - 5px)",
              color: "#333",
            }}
            minRows={8}
            maxRows={20}
            value={autogeneratePrompt}
            onChange={(event) => {
              const value = event.target.value
                .replace(/\s+/g, " ")
                .slice(0, MAX_CHARACTERS)

              setAutogeneratePrompt(value)
            }}
          />
          <div>{remainingCharacters} characters remaining</div>
          <section className="file-upload-container">
            <div {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} />
              <p>Drag 'n' drop some files here, or click to select files</p>
            </div>
            {filesToUpload.length ? (
              <aside>
                <h4>Upload files:</h4>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  {filesToUpload.map((file, i) => (
                    <div
                      key={file.name}
                      style={{
                        display: "flex",
                        marginLeft: 10,
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {fileUploadPreviews[i] ? (
                          <div
                            style={{ position: "relative" }}
                            title={file.name}
                          >
                            <img
                              src={fileUploadPreviews[i]}
                              alt={file.name}
                              style={{
                                width: 150,
                                height: 150,
                                objectFit: "cover",
                                marginRight: 8,
                                borderRadius: 4,
                                border: "1px solid #ccc",
                              }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                top: 6,
                                right: 16,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                              onClick={() => {
                                setFilesToUpload((prev) =>
                                  prev.filter((_, fileIndex) => fileIndex !== i)
                                )
                                setFileUploadPreviews((prev) =>
                                  prev.filter((_, fileIndex) => fileIndex !== i)
                                )
                              }}
                            >
                              <RedCrossIcon
                                style={{
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            </span>
                          </div>
                        ) : file.type === "application/pdf" ? (
                          <div
                            title={file.name}
                            style={{
                              border: "1px solid gray",
                              width: 150,
                              height: 150,
                              marginRight: 8,
                              fontSize: 32,
                              borderRadius: 4,
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            PDF
                            <span
                              style={{
                                position: "absolute",
                                top: 6,
                                right: 8,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                              onClick={() => {
                                setFilesToUpload((prev) =>
                                  prev.filter((_, fileIndex) => fileIndex !== i)
                                )
                                setFileUploadPreviews((prev) =>
                                  prev.filter((_, fileIndex) => fileIndex !== i)
                                )
                              }}
                            >
                              <RedCrossIcon
                                style={{
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </aside>
            ) : null}
          </section>
          <button
            className="api-button"
            onClick={handleConfirm}
            style={{ marginTop: 5, marginRight: 10 }}
          >
            Autogenerate
          </button>
        </div>
      )}
    </ReactModal>
  )
}
